// Package Imports
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// Hooks
import { useLocation } from 'react-router-dom';

// Components
import Modal from '../shared_components/Modal';
import NetworkCredentialsModal from './NetworkCredentialsModal';

// Action Imports
import {
  setDraggedUnit,
  setInitialUnitSet,
  setZephyrInEditMode,
  setZephyrs,
} from '../../actions/zephyrs';

// Util Imports
import utilsRequest from '../../utils/request';
import analyticsEventFirer from '../../utils/functions/analyticsEventFirer';
import {
  convertUnitTimeToLocalTime,
  getCurrentTimeZoneAbbreviation,
  getTimeOld,
} from '../../utils/functions/dateFinder';
import { getUnitTypeCountTxt } from '../../utils/functions/vZephyr';

// Const Imports
import { gaseousSlots, gtmEventIdentifiers } from '../../utils/consts';

// Asset Imports
import dragIcon from '../../assets/images/icon-drag.svg';

// Type Imports
import {
  Zephyr,
  ReduxState,
  VZephyrsConfig,
  ZephyrTypes,
  SlotsInfo,
  ZephyrCoverages,
  Loading,
} from '../../utils/interface';
import { setZephyrCoveragesList } from '../../actions/dataAnalytics';
import Spinner from '../shared_components/Spinner';
import { Box, Button, Tooltip } from '@mui/material';
import { setInitCursorPosition, setVZIconClicked } from '../../actions/addVZephyrs';

// Component Interfaces
interface InitState {
  editMode: boolean;
  editNetwork: Zephyr | null;
  expanded: boolean;
  nameInput: string;
  optionsMenuOpen: boolean;
  subscribed: boolean;
}

interface AsideUnitProps {
  unitList: Zephyr[];
  dataLoading: boolean;
  mappairPointMode: boolean;
  prevUnit: Zephyr | null;
  selectUnit: Function;
  selectedUnit: number;
  setColour: Function;
  setDraggedUnit: Function;
  setZephyrInEditMode: Function;
  setSelectedVZephyrToDelete: Function;
  unSubSelectedVZephyr: Function;
  setZephyrs: Function;
  unit: Zephyr;
  vZephyrsConfig: VZephyrsConfig;
  bearerToken: string | null;
  selectedUnitIdPod: number;
  setFilteredZephyrs: Function;
  filteredZephyrs: Zephyr[];
  zephyrCoveragesList: ZephyrCoverages[];
  setZephyrCoveragesList: Function;
  isDataAvailabilityChartLoading: boolean;
  isDeviceDropdownOpen: { 0: boolean, 1: boolean, 100: boolean };
  setIsDeviceDropdownOpen: Function;
  hasInitialUnitSet: boolean;
  setInitialUnitSet: Function;
  addVZIconClicked: boolean;
  setVZIconClicked: Function;
  setInitCursorPosition: Function;
  loading: Loading;
  noVZephyrs?: boolean;
}

// Component
const AsideUnit = ({
  unitList,
  dataLoading,
  mappairPointMode,
  prevUnit,
  selectUnit,
  selectedUnit,
  setColour,
  setDraggedUnit,
  setZephyrInEditMode,
  setZephyrs,
  unit,
  vZephyrsConfig,
  setSelectedVZephyrToDelete,
  unSubSelectedVZephyr,
  bearerToken,
  selectedUnitIdPod,
  setFilteredZephyrs,
  filteredZephyrs,
  zephyrCoveragesList,
  setZephyrCoveragesList,
  isDataAvailabilityChartLoading,
  isDeviceDropdownOpen,
  setIsDeviceDropdownOpen,
  hasInitialUnitSet,
  setInitialUnitSet,
  addVZIconClicked,
  setVZIconClicked,
  setInitCursorPosition,
  loading,
  noVZephyrs,
}: AsideUnitProps) => {
  // State
  const initState: InitState = {
    editMode: false,
    editNetwork: null,
    expanded: false,
    nameInput: unit?.name,
    optionsMenuOpen: false,
    subscribed: true,
  };
  const { t: translate } = useTranslation();

  const subscribed = initState.subscribed;
  const [editMode, setEditMode] = useState(initState.editMode);
  const [editNetwork, setEditNetwork] = useState(initState.editNetwork);
  const [expanded, setExpanded] = useState(initState.expanded);
  const [nameInput, setNameInput] = useState(initState.nameInput);
  const [optionsMenuOpen, setOptionsMenuOpenMenuOpen] = useState(
    initState.optionsMenuOpen,
  );
  const unitName = unit?.name;

  // Effects
  useEffect(() => {
    if (selectedUnit !== unit?.zNumber) {
      handleNameChangeReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit]);

  useEffect(() => {
    if (unitName) {
      setEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitName]);

  useEffect(() => {
    if (editMode) {
      unitInputRef.current!.focus();
    }
  }, [editMode]);

  // Consts
  const location = useLocation();
  const unitInputRef = useRef<HTMLInputElement | null>(null);
  const dragImage: HTMLImageElement = new Image();
  dragImage.src = dragIcon;

  // Functions
  const handleNameChange = (e: React.SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    setNameInput(target.value);
  };

  const handleNameChangeReset = () => {
    setEditMode(initState.editMode);
    setNameInput(initState.nameInput);
  };

  const handleNameSubmit = async (unitType: number) => {
    if (nameInput && nameInput !== unit.name) {
      // Format name input to have capitalize the start of every word
      const formattedNameInput = nameInput.replace(
        /(^\w{1})|(\s+\w{1})/g,
        (match) => match.toUpperCase(),
      );

      let res = null;
      if (unitType === ZephyrTypes.virtual) {
        res = await utilsRequest.editVZephyr(
          unit.zNumber,
          nameInput,
          bearerToken,
        );
      } else {
        res = await utilsRequest.createZephyrAlias(
          unit.zNumber,
          formattedNameInput,
          bearerToken,
        );
      }

      if (res) {
        const newZephyr = { ...unit, name: formattedNameInput };
        const newZephyrList = unitList.map((listUnit) => {
          if (listUnit.zNumber === unit.zNumber) {
            const ammendedUnit = newZephyr;
            return ammendedUnit;
          }
          return listUnit;
        });
        if (newZephyrList.length === unitList.length) {
          setZephyrInEditMode(newZephyr);
          setZephyrs(newZephyrList);
          setNameInput(formattedNameInput);
        }
      } else {
        handleNameChangeReset();
        setEditMode(false);
      }
    } else {
      handleNameChangeReset();
      setEditMode(false);
    }
  };

  const getCertificate = (zNumber: number) => {
    utilsRequest.downloadCalibrationCertificate(zNumber);
    // Analytics
    analyticsEventFirer(gtmEventIdentifiers.unitCertificateDownload);
  };

  const handleDragStart = (e: React.DragEvent) => {
    // Check for IE as it doesn't support setDragImage
    const isIE = !!(document as any).documentMode;
    if (!isIE) {
      e.dataTransfer.setDragImage(dragImage, 0, 20);
    }
    setDraggedUnit(unit);
  };

  const unitTypeAsString = (unitType: number) => {
    switch (unitType) {
      case 0:
        return 'Zephyrs';
      case 1:
        return 'AURN';
      case 100:
        return 'Virtual Zephyrs';

      default:
        return '';
    }
  };

  const getUnitCartridgeData = async (unit: Zephyr) => {
    if (!unit.slotsInfo) {
      const packagedSlotsInfo: SlotsInfo = {
        slotA: null,
        slotB: null,
      };
      if (!isUnitRetired) {
        for await (const gS of gaseousSlots) {
          const slotSerial = unit[`${gS.label}_SN`];
          if (slotSerial) {
            const slotInfo = await utilsRequest.getCartridge(
              slotSerial,
              bearerToken,
            );
            if (slotInfo) {
              packagedSlotsInfo[gS.label] = slotInfo;
            }
          }
        }
      }
      const copyZephyrsList = [...unitList];
      const newData = copyZephyrsList.find((z) => z.id_pod === unit.id_pod);
      if (newData) newData.slotsInfo = packagedSlotsInfo;
      setZephyrs(copyZephyrsList);
    }
  };

  const changeDateFormat = (date: string) => {
    return (
      date.split(' ')[0].split('-').reverse().join('-') +
      ' ' +
      date.split(' ')[1]
    );
  };

  const addUnitToAnalyticsList = (unit: Zephyr) => {
    setFilteredZephyrs((prevState: Zephyr[]) => {
      const unitToChange = prevState.find(
        (z: Zephyr) => z.zNumber === unit.zNumber,
      );
      if (unitToChange) {
        const unitIdx = prevState.indexOf(unitToChange);
        unitToChange.isSelected = !unitToChange.isSelected;
        prevState[unitIdx] = unitToChange;
      }
      return [...prevState];
    });
    setZephyrCoveragesList(unit);
  };

  const preventComma = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ',') {
      event.preventDefault();
    }
  };

  let isUnitRetired = false;
  if (unit?.userEndTimeDate) {
    const timestampDate = new Date(`${unit?.userEndTimeDate}Z`);
    const currentDate = new Date();
    isUnitRetired = currentDate > timestampDate;
  }

  const toggleDropdown = (event: any) => {
    if (event.target.id !== "vz-icon" && event.target.tagName !== "path" && !noVZephyrs) {
      setIsDeviceDropdownOpen((prevState: { 0: boolean, 1: boolean, 100: boolean }) => {
        return {
          ...prevState,
          0: unit.type === 0 ? !prevState[0] : false,
          1: unit.type === 1 ? !prevState[1] : false,
          100: unit.type === 100 ? !prevState[100] : false,
        };
      });
    }
  };

  const handleVZIconClick = (event: React.MouseEvent) => {
    setVZIconClicked(!addVZIconClicked);
    if (!addVZIconClicked) {
      setInitCursorPosition({ x: event.clientX, y: event.clientY })
    }
  };

  return (
    <>
      {!prevUnit || (prevUnit && prevUnit.type !== unit?.type) || prevUnit?.type === null || noVZephyrs ? (
        <Box className="unit-divider" sx={{ width: '100%', marginTop: 0, padding: 0 }}>
          <Button
            onClick={toggleDropdown}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              padding: 0,
              textAlign: 'left',
              textTransform: 'none',
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
            }}
          >
            <h4 style={{ margin: 0, paddingTop: 10, paddingBottom: 10 }}>
              {!noVZephyrs ? unitTypeAsString(unit?.type) : 'Virtual Zephyrs'}{' '}
              ({!noVZephyrs ? getUnitTypeCountTxt(unitList, unit?.type, vZephyrsConfig) : 0})
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {(unit?.type === 100 || noVZephyrs) &&
                <Tooltip
                  title={loading.zephyrLastHour || loading.aurnLastHour ? "Loading latest data..." : "Add Virtual Zephyr"}
                  arrow
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        display: !addVZIconClicked ? 'block' : 'none',
                        fontSize: '12px',
                        backgroundColor: '#CDD1D4',
                        color: '#2D3748',
                        '& .MuiTooltip-arrow': {
                          color: '#CDD1D4',
                        },
                      },
                    },
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" id="vz-icon" width="24" height="24" style={{ marginRight: 4 }} viewBox="0 0 24 24" fill="none" onClick={(e) => {
                    if (loading.zephyrLastHour || loading.aurnLastHour) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    handleVZIconClick(e);
                  }}>
                    <g clip-path="url(#clip0_6228_2481)">
                      <path d="M12 22C9.31667 19.7167 7.3125 17.5958 5.9875 15.6375C4.6625 13.6792 4 11.8667 4 10.2C4 7.7 4.80417 5.70833 6.4125 4.225C8.02083 2.74167 9.88333 2 12 2H12.5C12.6667 2 12.8333 2.01667 13 2.05V4.075C12.8333 4.04167 12.6708 4.02083 12.5125 4.0125C12.3542 4.00417 12.1833 4 12 4C10.3167 4 8.89583 4.57917 7.7375 5.7375C6.57917 6.89583 6 8.38333 6 10.2C6 11.3833 6.49167 12.7375 7.475 14.2625C8.45833 15.7875 9.96667 17.4833 12 19.35C14.0333 17.4833 15.5417 15.7875 16.525 14.2625C17.5083 12.7375 18 11.3833 18 10.2V10H20V10.2C20 11.8667 19.3375 13.6792 18.0125 15.6375C16.6875 17.5958 14.6833 19.7167 12 22ZM12 12C12.55 12 13.0208 11.8042 13.4125 11.4125C13.8042 11.0208 14 10.55 14 10C14 9.45 13.8042 8.97917 13.4125 8.5875C13.0208 8.19583 12.55 8 12 8C11.45 8 10.9792 8.19583 10.5875 8.5875C10.1958 8.97917 10 9.45 10 10C10 10.55 10.1958 11.0208 10.5875 11.4125C10.9792 11.8042 11.45 12 12 12ZM18 8H20V5H23V3H20V0H18V3H15V5H18V8Z" fill={!addVZIconClicked ? "#CDD1D4" : "#4E73C3"} />
                    </g>
                    <defs>
                      <clipPath id="clip0_6228_2481">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Tooltip>
              }
              {!noVZephyrs && <button className={`chevron-unit ${isDeviceDropdownOpen[unit?.type] ? 'closed' : ''}`} />}
            </div>
          </Button>
        </Box>
      ) : (
        <></>
      )}
      {unit && !noVZephyrs && <li
        key={unit.zNumber}
        style={{ display: isDeviceDropdownOpen[unit.type] ? 'block' : 'none' }}
        className={`unit-card unit-card-${unit.zNumber} ${selectedUnit === unit.zNumber ? 'active' : ''
          }${subscribed ? '' : 'hidden'}`}
        draggable={
          location.pathname.includes('data') &&
          !dataLoading &&
          selectedUnit !== unit.zNumber
        }
        onDragStart={(e) => handleDragStart(e)}
        data-user-tour={prevUnit === null ? "step-2" : undefined}
      >
        {/* <div
          className="aq-colour-strip"
          style={{
            backgroundColor: `${setColour(unit)}`,
          }}
        /> */}
        <div
          className={
            expanded &&
              (!mappairPointMode || location.pathname.includes('data'))
              ? unit.type === 0 && !isUnitRetired
                ? 'beacon-button active'
                : 'beacon-button aurn-active'
              : unit.type === 0 && !isUnitRetired
                ? 'beacon-button'
                : 'beacon-button no-toggle'
          }
          onClick={() => {
            if (!hasInitialUnitSet) setInitialUnitSet(true);
            if (
              !dataLoading &&
              selectedUnitIdPod !== unit.id_pod &&
              !location.pathname.includes('dataAnalytics')
            ) {
              selectUnit(unit);
            }
          }}
        >
          <header>
            {unit.type === 0 || unit.type === ZephyrTypes.virtual ? (
              <>
                <div className="selected-header">
                  {editMode ? (
                    <>
                      <input
                        ref={unitInputRef}
                        value={nameInput}
                        type="text"
                        onChange={(e) => handleNameChange(e)}
                        onBlur={() => handleNameSubmit(unit.type)}
                        onKeyDown={preventComma}
                        id="name-change-input"
                      />
                    </>
                  ) : (
                    <div className="header-wrapper">
                      <VZephyrBox
                        type={unit.type}
                        NO2={unit?.averageOfLastHourOfData?.NO2}
                        style={{
                          backgroundColor: `${setColour(unit)}`,
                        }}
                      />
                      <h4 id={`unit-header ${unit.type}`}>{unit.name}</h4>
                    </div>
                  )}

                  {!location.pathname.includes('analysis') ? (
                    <div className="options-container">
                      {(loading.zephyrLastHour || loading.aurnLastHour) && unit.type === 100 ?
                        <Tooltip
                          title="Loading latest data..."
                          arrow
                          placement="bottom"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: '12px',
                                backgroundColor: '#CDD1D4',
                                color: '#2D3748',
                                '& .MuiTooltip-arrow': {
                                  color: '#CDD1D4',
                                },
                              },
                            },
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              return;
                            }}
                          />
                        </Tooltip>
                        :
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOptionsMenuOpenMenuOpen(!optionsMenuOpen);
                          }}
                        />}
                      {optionsMenuOpen ? (
                        <ul
                          className="options-list"
                          onMouseLeave={() => setOptionsMenuOpenMenuOpen(false)}
                        >
                          <li
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditMode(true);
                            }}
                          >
                            {translate('AsideUnitEditName')}
                          </li>
                          {unit.type === ZephyrTypes.virtual ? (
                            <li
                              className={
                                vZephyrsConfig.new30Days >=
                                  vZephyrsConfig.new30DaysMax
                                  ? 'disabled'
                                  : ''
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (
                                  vZephyrsConfig.new30Days <=
                                  vZephyrsConfig.new30DaysMax
                                ) {
                                  setSelectedVZephyrToDelete(unit);
                                }
                              }}
                            >
                              {translate('VZUnitDeleteName')}
                            </li>
                          ) : (
                            <></>
                          )}

                          {unit.HasCertificate ? (
                            <li
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                getCertificate(unit.zNumber);
                              }}
                            >
                              {translate('AsideUnitCertificateDownloadButton')}
                            </li>
                          ) : (
                            <></>
                          )}
                        </ul>
                      ) : (
                        <></>
                      )}
                    </div>
                  ) : (
                    <>
                      {!isDataAvailabilityChartLoading && (
                        <label htmlFor={unit.name}>
                          <input
                            type="checkbox"
                            id={unit.name}
                            name={unit.name}
                            value={unit.name}
                            defaultChecked={unit.isSelected}
                            onChange={() => {
                              addUnitToAnalyticsList(unit);
                            }}
                          />
                        </label>
                      )}
                      {isDataAvailabilityChartLoading && (
                        <div
                          className="data-analytics-spinner"
                          style={{ position: 'absolute', right: 28 }}
                        >
                          <Spinner
                            additionalClass="header-spinner lds-spinner see-through"
                            on={isDataAvailabilityChartLoading}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!location.pathname.includes('analysis') && (
                  <div className="unit-headline-status">
                    {!isUnitRetired && unit.type !== ZephyrTypes.virtual ? (
                      <>
                        <ul className="unit-symbol-list">
                          <li>
                            <div className="unit-symbol-list-header unit-connection margin-center">
                              <div
                                className={`symbol-container ${parseFloat(unit.externalVoltage ?? '') >=
                                  16.5 &&
                                  getTimeOld(
                                    unit.lastConnectionDT,
                                    'YYYY-MM-DDZHH:mm:ss',
                                    'hours',
                                  ) <= 24
                                  ? 'mains'
                                  : parseFloat(unit.externalVoltage ?? '') >=
                                    16.5
                                    ? 'mains-active-grey'
                                    : 'mains-grey'
                                  }`}
                              >
                                <div className="unit-symbol-voltage" />
                                <div className="symbol-hover">
                                  {parseFloat(unit.externalVoltage ?? '') >=
                                    16.5 &&
                                    getTimeOld(
                                      unit.lastConnectionDT,
                                      'YYYY-MM-DDZHH:mm:ss',
                                      'hours',
                                    ) <= 24
                                    ? 'The Zephyr is running off external power.'
                                    : parseFloat(unit.externalVoltage ?? '') >=
                                      16.5
                                      ? 'Last known status running off external power.'
                                      : 'No External Power.'}
                                </div>
                              </div>
                            </div>
                            {expanded ? (
                              <>
                                <div className="unit-symbol-list-expanded">
                                  <div className="unit-expanded-descriptor">
                                    {'Power'}
                                  </div>
                                  <div className="unit-expanded-value">
                                    {parseFloat(unit.externalVoltage ?? '') >=
                                      17
                                      ? 'Connected'
                                      : 'N/A'}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <></>
                            )}
                          </li>
                          <li>
                            <div className="unit-symbol-list-header">
                              <div
                                className={`symbol-container margin-center ${translate(
                                  RenderBattery(
                                    parseFloat(unit.batteryVoltage ?? '') ?? 0,
                                    unit.batteryCharge ?? 0,
                                  )[0],
                                )}`}
                              >
                                <div
                                  className={`unit-symbol-battery ${getTimeOld(
                                    unit.lastConnectionDT,
                                    'YYYY-MM-DDZHH:mm:ss',
                                    'hours',
                                  ) >= 24
                                    ? 'grey'
                                    : ''
                                    }`}
                                />
                                <div className="symbol-hover">
                                  {getTimeOld(
                                    unit.lastConnectionDT,
                                    'YYYY-MM-DDZHH:mm:ss',
                                    'hours',
                                  ) >= 24
                                    ? 'Last known status: ' +
                                    translate(
                                      RenderBattery(
                                        parseFloat(
                                          unit.batteryVoltage ?? '',
                                        ) ?? 0,
                                        unit.batteryCharge ?? 0,
                                      )[2],
                                    )
                                    : translate(
                                      RenderBattery(
                                        parseFloat(
                                          unit.batteryVoltage ?? '',
                                        ) ?? 0,
                                        unit.batteryCharge ?? 0,
                                      )[2],
                                    )}
                                </div>
                              </div>
                              <div className="symbol-value"></div>
                            </div>
                            {expanded ? (
                              <>
                                <div className="unit-symbol-list-expanded">
                                  <div className="unit-expanded-descriptor">
                                    {translate('AsideUnitBatteryPower')}
                                  </div>
                                  <div className="unit-expanded-value">
                                    {translate(
                                      RenderBattery(
                                        parseFloat(
                                          unit.externalVoltage ?? '',
                                        ) ?? 0,
                                        unit.batteryCharge ?? 0,
                                      )[1],
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <></>
                            )}
                          </li>
                          <li>
                            <div className="unit-symbol-list-header unit-sampling margin-center">
                              <div
                                className={`symbol-container ${unit.lastDataDT === 'None' ? 'warning' : ''
                                  }`}
                              >
                                <div
                                  className={`unit-symbol-sampling ${RenderSampling(unit.SampPeriod)[1]
                                    }`}
                                />
                                <div className="symbol-hover">
                                  {translate(
                                    RenderSampling(unit.SampPeriod)[0],
                                  )}
                                </div>
                              </div>
                            </div>
                            {expanded ? (
                              <>
                                <div className="unit-symbol-list-expanded unit-sampling">
                                  <div>
                                    {RenderSampling(unit.SampPeriod)[2]}
                                  </div>
                                  <div className="unit-expanded-descriptor">
                                    {translate('AsideUnitSamplingData')}
                                  </div>
                                  <div className="unit-expanded-value">
                                    {unit.lastDataDT === 'None'
                                      ? translate('AsideUnitSamplingOff')
                                      : translate('AsideUnitSamplingOn')}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <></>
                            )}
                          </li>

                          <li>
                            <div className="unit-symbol-list-header unit-connection margin-center">
                              <div
                                className={`symbol-container ${getTimeOld(
                                  unit.lastConnectionDT,
                                  'YYYY-MM-DDZHH:mm:ss',
                                  'hours',
                                ) >= 24
                                  ? 'alert'
                                  : getTimeOld(
                                    unit.lastConnectionDT,
                                    'YYYY-MM-DDZHH:mm:ss',
                                    'hours',
                                  ) >= 2
                                    ? 'warning'
                                    : ''
                                  }`}
                              >
                                <div className="unit-symbol-connection" />
                                <div className="symbol-hover">
                                  {getTimeOld(
                                    unit.lastConnectionDT,
                                    'YYYY-MM-DDZHH:mm:ss',
                                    'hours',
                                  ) >= 24
                                    ? translate('AsideUnitConnectionAlert')
                                    : getTimeOld(
                                      unit.lastConnectionDT,
                                      'YYYY-MM-DDZHH:mm:ss',
                                      'hours',
                                    ) >= 2
                                      ? translate('AsideUnitConnectionWarning')
                                      : translate('AsideUnitConnectionExplainer')}
                                </div>
                              </div>
                            </div>
                            {expanded ? (
                              <>
                                <div className="unit-symbol-list-expanded">
                                  <div className="unit-expanded-descriptor">
                                    {translate('AsideUnitConnectionNetwork')}
                                  </div>
                                  <div className="unit-expanded-value">
                                    {translate('AsideUnitConnectionGSM')}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <></>
                            )}
                          </li>
                        </ul>
                        {expanded ? (
                          <>
                            <div className="lat-lon__wrapper">
                              <div className="lat-lon">
                                <dt>Location:&nbsp;</dt>
                                <dd className="lat-lon-value">
                                  <a
                                    href={`https://www.google.com/maps/search/${unit.latitude},${unit.longitude}?sa=X&ved=2ahUKEwi5xfbHvOX7AhUNWcAKHULtAvkQ8gF6BAgIEAE`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="location-lat-lon"
                                  >
                                    {unit.latitude}, {unit.longitude}
                                  </a>
                                </dd>
                              </div>
                            </div>
                            <div className="lat-lon__wrapper">
                              <div className="last-updated-location-title">
                                <dt>
                                  Updated:&nbsp;
                                  <span className="last-updated-location-time">
                                    {unit.latLngAltered
                                      ? changeDateFormat(unit.latLngAltered)
                                      : 'No last update information'}
                                  </span>
                                </dt>
                                {/* <dd className='last-updated-location-time' >{unit.latLngAltered}</dd> */}
                              </div>
                            </div>
                            <ul className="unit-symbol-list-metadata">
                              {gaseousSlots.map((gS) => (
                                <li key={gS.label}>
                                  {unit.slotsInfo ? (
                                    <div className="unit-symbol-list-header">
                                      <div className="symbol-container">
                                        <div
                                          className={`unit-cartridge

                                     ${unit.slotsInfo[gS.label]
                                              ? unit.slotsInfo[gS.label]!
                                                .CommercialName
                                              : 'test'
                                            }
                                     `}
                                        />

                                        <div className="symbol-hover">
                                          <webview
                                            dangerouslySetInnerHTML={{
                                              __html: translate(
                                                RenderCartridgeText(
                                                  unit.slotsInfo[gS.label]
                                                    ?.CommercialName,
                                                )[0],
                                              ),
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="unit-symbol-list-expanded">
                                        <div className="unit-expanded-descriptor">
                                          {gS.HTMLLabel}
                                        </div>
                                        <div className="unit-expanded-value">
                                          {unit.slotsInfo[gS.label]
                                            ? unit.slotsInfo[gS.label]!
                                              .CommercialName
                                            : translate(
                                              'AsideUnitCartridgeBlank',
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>Loading...</div>
                                  )}
                                </li>
                              ))}
                              {/* <li>
                            <div className="unit-symbol-list-header">
                              <div className="symbol-container">
                                <div className="unit-station met" />
                              </div>
                            </div>
                            <div className="unit-symbol-list-expanded">
                              <div className="unit-expanded-descriptor">
                                Sensors
                              </div>
                              <div className="unit-expanded-value">Met</div>
                            </div>
                          </li> */}
                            </ul>
                          </>
                        ) : (
                          <></>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                )}
              </>
            ) : location.pathname.includes('analysis') ? (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4>{unit.name}</h4>
                {!isDataAvailabilityChartLoading && (
                  <label htmlFor={unit.name}>
                    <input
                      type="checkbox"
                      id={unit.name}
                      name={unit.name}
                      value={unit.name}
                      defaultChecked={unit.isSelected}
                      onChange={() => {
                        addUnitToAnalyticsList(unit);
                      }}
                      style={{ marginTop: 0 }}
                    />
                  </label>
                )}
                {isDataAvailabilityChartLoading && (
                  <div
                    className="data-analytics-spinner"
                    style={{ position: 'absolute', right: 28 }}
                  >
                    <Spinner
                      additionalClass="header-spinner lds-spinner see-through"
                      on={isDataAvailabilityChartLoading}
                    />
                  </div>
                )}
              </div>
            ) : (
              <h4>{unit.name}</h4>
            )}
          </header>

          {unit.type === ZephyrTypes.virtual ? (
            <></>
          ) : (
            <div className="unit-updated">
              <div className="unit-updated-info">
                {unit.type === 0 ? (
                  <dl>
                    {isUnitRetired ? (
                      <>
                        <dt>{translate('AsideUnitStatusRetired')}</dt>
                        <dd>
                          {moment(
                            unit.userEndTimeDate,
                            'YYYY-MM-DDZHH:mm:ss',
                          ).format('DD-MM-YYYY HH:mm')}
                        </dd>
                      </>
                    ) : (
                      <>
                        <dt>{translate('AsideUnitUpdatedHeader')}</dt>
                        <dd>
                          {unit.lastConnectionDT
                            ? `${moment(
                              convertUnitTimeToLocalTime(
                                unit.lastConnectionDT,
                              ),
                              'YYYY-MM-DDZHH:mm:ss',
                            ).format(
                              'DD-MM-YYYY HH:mm',
                            )} ${getCurrentTimeZoneAbbreviation()}`
                            : 'NA'}
                        </dd>
                      </>
                    )}
                  </dl>
                ) : (
                  <></>
                )}
                {unit.type === 0 ? (
                  <div className="unit-updated-separator">·</div>
                ) : (
                  <></>
                )}
                {unit.type === 0 ? (
                  <>
                    <dl style={{ display: 'flex' }}>
                      <dt>{translate('AsideUnitIdHeader')}</dt>
                      <dd>{unit.zNumber}</dd>
                    </dl>
                  </>
                ) : (unit.type as any) === ZephyrTypes.virtual ? (
                  <dl>
                    <dt>{translate('AsideUnitType')}</dt>
                    <dd>{translate('AsideUnitTypeVirtualZephyr')}</dd>
                  </dl>
                ) : (
                  <dl>
                    <dt>{translate('AsideUnitType')}</dt>
                    <dd>{translate('AsideUnitTypeAURN')}</dd>
                  </dl>
                )}
              </div>
              {unit.type === 0 &&
                !isUnitRetired &&
                !location.pathname.includes('analysis') ? (
                <button
                  type="button"
                  className={expanded ? 'expanded' : ''}
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!dataLoading) {
                      setExpanded(!expanded);
                      if (!expanded) {
                        await getUnitCartridgeData(unit);
                      }
                    }
                  }}
                />
              ) : (
                <div className="expand-placeholder" />
              )}
            </div>
          )}
        </div>
      </li>}

      {!noVZephyrs && <Modal
        on={!!editNetwork}
        modalComponent={
          <NetworkCredentialsModal
            closeModal={() => setEditNetwork(null)}
            confirmSubmit={() => { }}
            unit={editNetwork}
          />
        }
      />}
    </>
  );
};

function RenderSampling(samplingPeriod: number | null) {
  // const { t: translate } = useTranslation();

  if (samplingPeriod) {
    if (samplingPeriod >= 7 && samplingPeriod <= 20) {
      //return [translate('AsideUnitSamplingExplainerHigh'), 'High', 'High'];
      return ['AsideUnitSamplingExplainerHigh', 'High', 'High'];
      //return ['Cannot be determined', 'grey', 'N/A'];
    } else if (samplingPeriod >= 50 && samplingPeriod <= 70) {
      //return [translate('AsideUnitSamplingExplainerMed'), 'Normal', 'Normal'];
      return ['AsideUnitSamplingExplainerMed', 'Normal', 'Normal'];
      //return ['Cannot be determined', 'grey', 'N/A'];
    } else {
      return ['Cannot be determined', 'grey', 'N/A'];
      //return [translate('AsideUnitSamplingExplainerMed'), 'Normal', 'Normal'];
    }
  } else {
    return ['Cannot be determined', 'grey', 'N/A'];
    //return [translate('AsideUnitSamplingExplainerMed'), 'Normal', 'Normal'];
  }
}

function RenderCartridgeText(cartridgeLabel: string | undefined) {
  //const { t: translate } = useTranslation();

  if (cartridgeLabel) {
    if (cartridgeLabel === 'Standard') {
      //return translate('AsideUnitCartridgeExplainerStandard');
      return 'AsideUnitCartridgeExplainerStandard';
    } else if (cartridgeLabel === 'Enhanced') {
      //return translate('AsideUnitCartridgeExplainerEnhanced');
      return 'AsideUnitCartridgeExplainerEnhanced';
    } else if (cartridgeLabel === 'Enhanced+') {
      //return translate('AsideUnitCartridgeExplainerEnhancedPlus');
      return 'AsideUnitCartridgeExplainerEnhancedPlus';
    } else if (cartridgeLabel === 'Enhanced++') {
      //return translate('AsideUnitCartridgeExplainerEnhancedPP');
      return 'AsideUnitCartridgeExplainerEnhancedPP';
    }
  }
  return '';
}

const VZephyrBox = ({ type, NO2, style }: any) => {
  if (type !== 100) return null;
  return <div className="vzephyr-color-box" style={style} />;
};

function RenderBattery(batteryVoltage: number, batteryCharge: number) {
  //const { t: translate } = useTranslation();

  if (!isNaN(batteryVoltage)) {
    if (batteryVoltage > 17.5) {
      return [
        'charging',
        //translate('AsideUnitBatteryCharging'),
        //translate('AsideUnitBatteryChargingDesc'),
        'AsideUnitBatteryCharging',
        'AsideUnitBatteryChargingDesc',
      ];
    } else if (batteryCharge < 5 || batteryVoltage === 0) {
      return [
        'alert',
        //translate('AsideUnitBatteryFlat'),
        //translate('AsideUnitBatteryFlatDesc'),
        'AsideUnitBatteryFlat',
        'AsideUnitBatteryFlatDesc',
      ];
    } else if (batteryCharge < 30) {
      return [
        'warning',
        //translate('AsideUnitBatteryDischarging'),
        //translate('AsideUnitBatteryFlatDesc'),
        'AsideUnitBatteryDischarging',
        'AsideUnitBatteryFlatDesc',
      ];
    } else {
      return [
        'charging',
        //translate('AsideUnitBatteryDischarging'),
        //translate('AsideUnitBatteryDischargingDesc'),
        'AsideUnitBatteryDischarging',
        'AsideUnitBatteryDischargingDesc',
      ];
    }
  } else {
    return [
      'none',
      //translate('AsideUnitBatteryFlat'),
      //translate('AsideUnitBatteryFlatDesc'),
      'AsideUnitNoBattery',
      'AsideUnitNoBattery',
    ];
  }
}

// Redux
const mapStateToProps = (state: ReduxState) => ({
  loading: state.loading,
  unitList: state.getZephyrs.zephyrs,
  bearerToken: state.auth.bearerToken,
  zephyrCoveragesList: state.unitHistoriesOptions.zephyrCoveragesList,
  isDataAvailabilityChartLoading: state.unitHistoriesOptions.isDataAvailabilityChartLoading,
  hasInitialUnitSet: state.setZephyr.hasInitialSet,
  addVZIconClicked: state.addVZephyrs.addVZIconClicked,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setDraggedUnit,
      setZephyrInEditMode,
      setZephyrs,
      setZephyrCoveragesList,
      setInitialUnitSet,
      setVZIconClicked,
      setInitCursorPosition,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AsideUnit);
